import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { apiClient, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import {
  Sparkles, Crown, Zap, CheckCircle2, Loader2, ArrowRight, AlertTriangle, Star,
} from "lucide-react";
import { launchPrice } from "../lib/launchDiscount";

const TIER_ICONS = { spotlight_7: Zap, spotlight_14: Sparkles, premium_30: Crown };

export default function BoostListingPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { lang } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [vehicle, setVehicle] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [chosenTier, setChosenTier] = useState("spotlight_7");
  const [loadErr, setLoadErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [success, setSuccess] = useState(false);
  const boostStatus = params.get("boost_status");

  useEffect(() => {
    apiClient.get(`/vehicles/${id}`).then(({ data }) => setVehicle(data)).catch((e) => setLoadErr(e.message));
    apiClient.get("/vehicles/boost/pricing").then(({ data }) => setTiers(data.tiers)).catch(() => {});
  }, [id]);

  // If returning from Stripe success, poll sync
  useEffect(() => {
    if (boostStatus === "success" && vehicle && !success) {
      setSyncing(true);
      apiClient
        .post(`/vehicles/${id}/boost/sync`)
        .then(({ data }) => {
          if (data.paid) {
            setSuccess(true);
            setVehicle(data.vehicle);
          }
        })
        .catch(() => {})
        .finally(() => setSyncing(false));
    }
  }, [boostStatus, vehicle, id, success]);

  if (user === undefined) return <div className="p-10 text-[#6b6b6b]">…</div>;
  if (!user) return <Navigate to="/login" state={{ from: `/vehicle/${id}/boost` }} replace />;
  if (loadErr) return <div className="p-10 text-red-600">{loadErr}</div>;
  if (!vehicle || !tiers) return <div className="p-10 text-[#6b6b6b]">Loading…</div>;

  const isOwner = vehicle.seller_id === user.id || vehicle.owner_id === user.id;
  const isAdmin = ["admin", "super_admin"].includes(user.role);
  if (!isOwner && !isAdmin) {
    return (
      <div className="max-w-[800px] mx-auto p-10 text-center">
        <AlertTriangle className="w-10 h-10 text-[#8a6f2e] mx-auto mb-3" />
        <h2 className="font-display text-2xl mb-2">
          {lang === "ar" ? "هذا الإعلان ليس لك" : "Not your listing"}
        </h2>
        <Link to={`/vehicle/${id}`} className="btn-primary mt-4 inline-flex">
          {lang === "ar" ? "العودة للإعلان" : "Back to listing"}
        </Link>
      </div>
    );
  }

  const startCheckout = async () => {
    // Route through the Moyasar hosted checkout — replaces legacy Stripe redirect.
    // Boost SKUs (spotlight_7 | spotlight_14 | premium_30) are already in the
    // Moyasar catalogue with the exact same prices.
    const params = new URLSearchParams({
      purpose: "listing_fee",
      sku: chosenTier,
      reference_id: String(id),
    });
    nav(`/checkout/moyasar?${params.toString()}`);
  };

  const alreadyBoosted = vehicle.boost_active;
  const expiresOn = vehicle.boosted_until
    ? new Date(vehicle.boosted_until).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB")
    : null;

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="boost-listing-page">
      <Link to={`/vehicle/${id}`} className="text-sm text-[#6b6b6b] hover:text-[#0a0a0a]">
        ← {lang === "ar" ? "العودة للإعلان" : "Back to listing"}
      </Link>

      <div className="mt-6">
        <div className="overline overline-gold mb-3">
          {lang === "ar" ? "أعلى نتائج البحث" : "TOP OF SEARCH RESULTS"}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium text-[#0a0a0a] leading-[1.05]">
          {lang === "ar" ? "ميّز إعلانك واحصل على مشترين أسرع" : "Boost your listing — sell faster"}
        </h1>
        <p className="text-[#525252] mt-3 max-w-2xl text-base sm:text-lg leading-relaxed">
          {lang === "ar"
            ? "ثبّت سيارتك في أعلى نتائج البحث، واحصل على شارة ذهبية «مميّز» تجذب المشترين الجادّين."
            : "Pin your listing to the top of search and earn a gold \"Featured\" badge that draws serious buyers."}
        </p>
      </div>

      {/* Success state */}
      {success && (
        <div className="mt-8 surface rounded-3xl p-8 text-center" data-testid="boost-success">
          <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-3" />
          <h2 className="font-display text-2xl text-[#0a0a0a] mb-2">
            {lang === "ar" ? "تم تفعيل التمييز بنجاح" : "Boost activated"}
          </h2>
          <p className="text-sm text-[#525252]">
            {lang === "ar"
              ? `إعلانك الآن في القمة حتى ${expiresOn}.`
              : `Your listing is now at the top until ${expiresOn}.`}
          </p>
          <Link to={`/vehicle/${id}`} className="btn-primary mt-5 inline-flex">
            {lang === "ar" ? "عرض الإعلان" : "View listing"}
          </Link>
        </div>
      )}

      {/* Already boosted */}
      {!success && alreadyBoosted && (
        <div
          className="mt-8 rounded-2xl p-5 border-2"
          style={{ borderColor: "#c9a55c", backgroundColor: "rgba(201,165,92,0.08)" }}
          data-testid="boost-already-active"
        >
          <div className="flex items-center gap-3">
            <Star className="w-6 h-6 text-[#8a6f2e]" />
            <div>
              <div className="font-semibold text-[#5a4615]">
                {lang === "ar" ? "إعلانك مميّز حالياً" : "Your listing is currently boosted"}
              </div>
              <div className="text-xs text-[#8a6f2e] mt-0.5">
                {lang === "ar" ? `ساري حتى ${expiresOn} — يمكنك تمديد الفترة بشراء حزمة إضافية.` : `Active until ${expiresOn} — you can extend by buying another tier.`}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle being boosted summary */}
      <div className="surface rounded-2xl p-5 mt-8 flex items-center gap-4" data-testid="boost-vehicle-summary">
        <img
          src={(vehicle.images && vehicle.images[0]) || vehicle.image || "https://placehold.co/120x80/eee/999?text=Car"}
          alt=""
          className="w-24 h-16 object-cover rounded-lg border border-[#0a0a0a]/10"
        />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-[#8a6f2e] font-semibold">{vehicle.year} · {vehicle.make}</div>
          <div className="font-display text-lg truncate">{vehicle.model}</div>
          <div className="text-sm text-[#6b6b6b]">{vehicle.city}</div>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl text-[#8a6f2e]">{Number(vehicle.price_sar).toLocaleString()}</div>
          <div className="text-xs text-[#6b6b6b]">SAR</div>
        </div>
      </div>

      {/* Tiers */}
      {!success && (
        <div className="mt-8" data-testid="boost-tiers">
          <div className="overline overline-gold mb-3">{lang === "ar" ? "اختر باقتك" : "PICK A TIER"}</div>
          <div className="grid sm:grid-cols-3 gap-4">
            {Object.entries(tiers).map(([id, tier]) => {
              const Icon = TIER_ICONS[id] || Sparkles;
              const isSelected = chosenTier === id;
              const isPremium = id === "premium_30";
              return (
                <button
                  type="button"
                  key={id}
                  onClick={() => setChosenTier(id)}
                  className={`relative text-start rounded-2xl p-5 border-2 transition-all ${
                    isSelected
                      ? "border-[#c9a55c] bg-gradient-to-br from-[#fdf9ef] to-white shadow-lg"
                      : "border-[#0a0a0a]/10 hover:border-[#c9a55c]/60 bg-white"
                  }`}
                  data-testid={`boost-tier-${id}`}
                >
                  {isPremium && (
                    <span className="absolute -top-3 inline-block px-2.5 py-0.5 rounded-full bg-[#0a0a0a] text-[#c9a55c] text-[10px] font-bold tracking-wide">
                      {lang === "ar" ? "الأكثر طلباً" : "MOST POPULAR"}
                    </span>
                  )}
                  {/* iter106 — launch discount corner ribbon */}
                  {launchPrice(tier.price_sar).active && (
                    <div
                      className="absolute -top-2 -end-2 z-10 rounded-bl-2xl rounded-tr-2xl px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider shadow-lg"
                      style={{
                        background: "linear-gradient(135deg,#ef4444 0%,#b91c1c 100%)",
                        color: "#ffffff",
                        border: "1px solid rgba(255,255,255,0.3)",
                      }}
                      data-testid={`boost-launch-ribbon-${id}`}
                    >
                      {lang === "ar" ? "خصم 50٪" : "50% OFF"}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${isSelected ? "text-[#8a6f2e]" : "text-[#525252]"}`} />
                    <div className="font-display text-lg text-[#0a0a0a]">
                      {lang === "ar" ? tier.label_ar : tier.label_en}
                    </div>
                  </div>
                  <div className="text-xs text-[#525252] mt-2 leading-relaxed">
                    {lang === "ar" ? tier.tagline_ar : tier.tagline_en}
                  </div>
                  {(() => {
                    const info = launchPrice(tier.price_sar);
                    return (
                      <div className="mt-4 flex flex-col gap-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-display text-3xl font-black text-[#0a0a0a]" data-testid={`boost-current-price-${id}`}>{info.current}</span>
                          <span className="text-xs text-[#6b6b6b]">SAR / {tier.days} {lang === "ar" ? "يوم" : "days"}</span>
                          {info.active && (
                            <span
                              className="font-display text-lg line-through decoration-2"
                              style={{ color: "rgba(10,10,10,0.4)", textDecorationColor: "#ef4444" }}
                              data-testid={`boost-original-price-${id}`}
                            >
                              {info.original}
                            </span>
                          )}
                        </div>
                        {info.active && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-extrabold rounded-full px-2 py-0.5"
                              style={{ background: "rgba(239,68,68,0.15)", color: "#b91c1c", border: "1px solid rgba(239,68,68,0.5)" }}
                              data-testid={`boost-savings-pill-${id}`}
                            >
                              {lang === "ar" ? `وفّرت ${info.savings} ر.س` : `Save ${info.savings} SAR`}
                            </span>
                            <span className="text-[10px] font-bold text-red-600">
                              {lang === "ar" ? "عرض إطلاق" : "Launch offer"}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Benefits */}
      {!success && (
        <div className="mt-8 grid md:grid-cols-3 gap-4" data-testid="boost-benefits">
          {[
            { ar: "تثبيت في أعلى نتائج السوق", en: "Pinned to the top of marketplace search" },
            { ar: "شارة ذهبية «مميّز» تجذب الانتباه", en: "Gold \"Featured\" badge that grabs attention" },
            { ar: "ظهور أكبر = مشتري أسرع وسعر أفضل", en: "More views = faster sale at a better price" },
          ].map((b) => (
            <div key={b.en} className="rounded-2xl border border-[#0a0a0a]/10 bg-white p-4 flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
              <span className="text-sm text-[#3a3a3a] leading-snug">{lang === "ar" ? b.ar : b.en}</span>
            </div>
          ))}
        </div>
      )}

      {!success && (
        <div className="mt-8">
          <button
            type="button"
            onClick={startCheckout}
            disabled={busy || syncing}
            className="btn-primary w-full sm:w-auto justify-center text-base px-8 py-3"
            data-testid="boost-pay-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {(() => {
              const info = launchPrice(tiers[chosenTier].price_sar);
              if (info.active) {
                return lang === "ar"
                  ? `ادفع ${info.current} ريال فقط · بدلاً من ${info.original} — عرض إطلاق`
                  : `Pay ${info.current} SAR · was ${info.original} — Launch offer`;
              }
              return lang === "ar"
                ? `ادفع ${tiers[chosenTier].price_sar} ريال وفعّل الآن`
                : `Pay ${tiers[chosenTier].price_sar} SAR & activate`;
            })()}
          </button>
          {syncing && (
            <div className="mt-3 text-xs text-[#6b6b6b] inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {lang === "ar" ? "نتحقق من حالة الدفع…" : "Confirming payment…"}
            </div>
          )}
          <p className="text-[11px] text-[#8a8a8a] mt-4 leading-relaxed max-w-md">
            {lang === "ar"
              ? "الدفع آمن عبر Moyasar (مدى / Visa / Mastercard / Apple Pay). تُفعّل الباقة فور تأكيد الدفع، ولا يمكن استرداد المبلغ بعد التفعيل."
              : "Secure payment via Moyasar (Mada / Visa / Mastercard / Apple Pay). The boost activates immediately on payment and is non-refundable once activated."}
          </p>
        </div>
      )}
    </div>
  );
}
